import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Avatar, Dropdown, Layout, Menu, Message } from '@arco-design/web-react';
import { IconDown, IconLanguage, IconPoweroff, IconQuestionCircle, IconUser } from '@arco-design/web-react/icon';
import { useTranslation } from 'react-i18next';
import type { GlobalState } from '@/store';
import { logout as logoutApi } from '@/services/auth';
import { clearAuthMarkerCookie } from '@/utils/authSession';
import { useLanguageStore } from '@/store/useLanguageStore';
import styles from './style.module.less';

const { Header } = Layout;

export default function NavBar() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const lang = useLanguageStore((s) => s.lang);
  const setLang = useLanguageStore((s) => s.setLang);
  const user = useSelector((state: GlobalState) => state.user);
  const collapsed = useSelector((state: GlobalState) => state.sidebarCollapsed);

  const handleLogout = async () => {
    try {
      await logoutApi();
    } catch {
      // Session may already be invalid.
    } finally {
      dispatch({ type: 'update-user', payload: { user: null } });
      clearAuthMarkerCookie();
      Message.success(t('nav.logoutSuccess'));
      navigate('/login');
    }
  };

  const langMenu = (
    <Menu>
      <Menu.Item key="zh-CN" onClick={() => setLang('zh-CN')}>
        中文
      </Menu.Item>
      <Menu.Item key="en" onClick={() => setLang('en')}>
        English
      </Menu.Item>
    </Menu>
  );

  const droplist = (
    <Menu>
      <Menu.Item key="profile" onClick={() => navigate('/profile')}>
        <IconUser className={styles.dropdownIcon} />
        {t('nav.profile')}
      </Menu.Item>
      <Menu.Item key="logout" onClick={() => void handleLogout()}>
        <IconPoweroff className={styles.dropdownIcon} />
        {t('nav.logout')}
      </Menu.Item>
    </Menu>
  );

  return (
    <Header className={styles.navbar}>
      <div className={collapsed ? `${styles.left} ${styles.leftCollapsed}` : styles.left}>
        <span className={styles.logo}>
          {!collapsed && (
            <img className={styles.logoImg} src="/letsfly-logo.svg" alt="LetsFly" />
          )}
        </span>
      </div>
      <div className={styles.right}>
        <button
          type="button"
          className={styles.help}
          title={t('nav.help')}
          onClick={() => navigate('/help')}
        >
          <IconQuestionCircle />
          <span>{t('nav.help')}</span>
        </button>
        <Dropdown droplist={langMenu} position="br" trigger="click">
          <div className={styles.langSwitcher}>
            <IconLanguage />
            <span>{lang === 'en' ? 'English' : '中文'}</span>
            <IconDown className={styles.caret} />
          </div>
        </Dropdown>
        <Dropdown droplist={droplist} position="br">
          <div className={styles.user}>
            <Avatar size={28} style={{ backgroundColor: 'rgb(var(--primary-6))' }}>
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt={user.name} />
              ) : (
                user?.name?.charAt(0)?.toUpperCase() || <IconUser />
              )}
            </Avatar>
            <span className={styles.username}>{user?.name || t('nav.userFallback')}</span>
            <IconDown className={styles.caret} />
          </div>
        </Dropdown>
      </div>
    </Header>
  );
}
